import {
	VelavuAsset,
	VelavuDevice,
	VelavuStreamUpdateData,
	VelavuStreamUpdateOperation,
} from "velavu-js-api";
import { useCallback } from "react";
import { useSubscribeCombinedDeviceUpdates } from "./stream-helper";

//An object that represents an update to a trackable object
export interface ObjectUpdate<D> {
	// The ID of the updated object
	id: string;
	// The object data to apply, or null if the object was deleted
	data: D | null;
}

export type AssetUpdate = ObjectUpdate<VelavuAsset>;
export type DeviceUpdate = ObjectUpdate<VelavuDevice>;

export function objectUpdateFromStreamUpdate<D>(
	update: VelavuStreamUpdateData<D>,
): ObjectUpdate<D> {
	switch (update.operation) {
		case VelavuStreamUpdateOperation.Created:
		case VelavuStreamUpdateOperation.Updated:
			return {
				id: update.id,
				data: update.body,
			};
		case VelavuStreamUpdateOperation.Deleted:
			return {
				id: update.id,
				data: null,
			};
	}
}

export function applyObjectUpdate<D>(
	update: ObjectUpdate<D>,
	objects: D[],
	getID: (object: D) => string,
): D[] {
	const index = objects.findIndex((object) => getID(object) === update.id);

	if (update.data === null) {
		if (index === -1) {
			return objects;
		}

		const updatedObjects = [...objects];
		updatedObjects.splice(index, 1);
		return updatedObjects;
	}

	if (index === -1) {
		return [...objects, update.data];
	}

	const updatedObjects = [...objects];
	updatedObjects[index] = update.data;
	return updatedObjects;
}

export function applyDeviceUpdate<D extends VelavuDevice>(
	update: ObjectUpdate<VelavuDevice>,
	devices: D[],
	filter: (device: VelavuDevice) => device is D,
): D[];
export function applyDeviceUpdate(
	update: ObjectUpdate<VelavuDevice>,
	devices: VelavuDevice[],
): VelavuDevice[];
export function applyDeviceUpdate<D extends VelavuDevice>(
	update: ObjectUpdate<VelavuDevice>,
	devices: VelavuDevice[],
	filter?: (device: VelavuDevice) => device is D,
): VelavuDevice[] {
	if (update.data !== null) {
		//Find the device to update
		const index = devices.findIndex((device) => device.id === update.id);

		//If the device wasn't found, try to add it
		if (index === -1) {
			//If the new device matches the filter, add it to the array
			if (filter === undefined || filter(update.data)) {
				return [...devices, update.data];
			} else {
				return devices;
			}
		} else {
			const newDevices = [...devices];

			if (filter === undefined || filter(update.data)) {
				//Add the device
				newDevices[index] = update.data;
			} else {
				//This device no longer matches the filter; remove it
				newDevices.splice(index, 1);
			}

			return newDevices;
		}
	} else {
		const index = devices.findIndex((device) => device.id === update.id);
		if (index === -1) {
			return devices;
		}
		const newDevices = [...devices];
		newDevices.splice(index, 1);
		return newDevices;
	}
}

/**
 * Subscribes a device array to device updates
 */
export function useSyncStreamToDeviceArray<
	D extends VelavuDevice,
	T extends D[] | null | undefined,
>(
	setDevices: (updater: (devices: T) => T) => void,
	filter: (device: VelavuDevice) => device is D,
): void;
export function useSyncStreamToDeviceArray<
	T extends VelavuDevice[] | null | undefined,
>(setDevices: (updater: (devices: T) => T) => void): void;
export function useSyncStreamToDeviceArray<
	D extends VelavuDevice,
	T extends D[] | null | undefined,
>(
	setDevices: (updater: (devices: T) => T) => void,
	filter?: (device: VelavuDevice) => device is D,
) {
	useSubscribeCombinedDeviceUpdates(
		useCallback(
			(update) => {
				setDevices((devices) => {
					if (Array.isArray(devices)) {
						return applyDeviceUpdate(
							update,
							devices,
							filter ?? ((d): d is D => true),
						) as T;
					} else {
						return devices;
					}
				});
			},
			[filter, setDevices],
		),
	);
}
